import { Modal, ModalBody, ModalFooter } from '@coolblue-development/freddy';
import { ResolvedGA4Trigger } from '@coolblue-development/ga4-events';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import type { SecondChanceProductState } from '@features/second-chance/typings';
import { ReactNode } from 'react';

import type { InitialProduct } from './Collection.types';

type MobileModalProps<T> = {
  isInternalUser?: boolean;
  isOpen: boolean;
  setIsOpen: (state: boolean) => void;
  setSelectedState?: (state: SecondChanceProductState) => void;
  initialState?: SecondChanceProductState | null;
  setSelectedProduct: (product: T) => void;
  initialSelectedProduct: T;
  trackingEvent?: ResolvedGA4Trigger;
  collectionTitle: string;
  modalBody: ReactNode;
  modalFooter?: ReactNode;
  fullHeight?: boolean;
};

export const MobileModal = <T extends InitialProduct>({
  isInternalUser = false,
  isOpen,
  setIsOpen,
  setSelectedState,
  initialState,
  setSelectedProduct,
  initialSelectedProduct,
  trackingEvent,
  collectionTitle,
  modalBody,
  modalFooter,
  fullHeight = false
}: MobileModalProps<T>) => {
  const modalTranslations = useFreddyTranslations('modal');
  const { trackEvent } = useAnalytics();

  return (
    <Modal
      {...(isInternalUser && { 'data-cy': 'modal-collection' })}
      translations={modalTranslations}
      open={isOpen}
      onClose={() => {
        setIsOpen(false);
        setSelectedProduct(initialSelectedProduct); // Reset values when closed
        if (trackingEvent) {
          trackEvent(trackingEvent);
        }
        if (setSelectedState && initialState) {
          setSelectedState(initialState);
        }
      }}
      placement='bottom'
      title={collectionTitle}
      size='xlarge'
      fullHeight={fullHeight}
      body={<ModalBody>{modalBody}</ModalBody>}
      {...(modalFooter && { footer: <ModalFooter>{modalFooter}</ModalFooter> })}
    />
  );
};
